/**
 * Diff/patch engine for the voice-call collaborative markdown editor.
 *
 * The agent dispatches structured edits via the `patch_editor` client tool
 * (registered in `useVoiceSession.ts`). This module is the pure, framework-free
 * core that applies those edits to a string of markdown content.
 *
 * Keeping this isolated in `/lib` means it is trivially unit-testable and
 * reusable from any component that wants to preview or dry-run a patch set.
 */

export type PatchOp =
  | { op: 'set'; text: string }
  | { op: 'append'; text: string }
  | { op: 'prepend'; text: string }
  | {
      op: 'replace';
      find: string;
      replace: string;
      replace_all?: boolean;
    }
  | { op: 'insert_after'; anchor: string; text: string }
  | { op: 'insert_before'; anchor: string; text: string }
  | { op: 'delete'; find: string; delete_all?: boolean };

export interface ApplyPatchesResult {
  /** Content after all *successfully* applied ops. Failed ops are skipped. */
  content: string;
  /** Human-readable error messages, one per failed op (empty if all succeeded). */
  errors: string[];
  /** Number of ops that applied successfully. */
  appliedCount: number;
}

/** Count non-overlapping occurrences of `needle` in `haystack`. */
const countOccurrences = (haystack: string, needle: string): number => {
  if (!needle) return 0;
  let count = 0;
  let from = 0;
  let idx = haystack.indexOf(needle, from);
  while (idx !== -1) {
    count++;
    from = idx + needle.length;
    idx = haystack.indexOf(needle, from);
  }
  return count;
};

/** Short preview of a string for error messages. */
const preview = (s: string, max = 60): string => {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
};

/**
 * Apply a sequence of patch operations to a piece of markdown content.
 *
 * Ops run in order; each op sees the result of the previous ops. A failing op
 * is skipped (its error is recorded) rather than aborting the whole batch,
 * because the agent often dispatches several small ops in one tool call and
 * partial progress is far better than all-or-nothing.
 *
 * Invariants the agent can rely on:
 *   - `find` / `anchor` must uniquely match the current content unless the op
 *     explicitly opts in to `replace_all` / `delete_all`.
 *   - Matching is literal string matching (no regex, no fuzzy), so the agent's
 *     `find` must be an exact substring.
 *   - On failure, the error message names the op and the offending string so
 *     the model can self-correct on the next turn.
 */
export function applyPatches(
  content: string,
  ops: PatchOp[],
): ApplyPatchesResult {
  const errors: string[] = [];
  let working = content;
  let applied = 0;

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    try {
      switch (op.op) {
        case 'set': {
          working = op.text;
          applied++;
          break;
        }
        case 'append': {
          // Keep paragraph break if we're joining non-empty content.
          const sep = working.length > 0 && !working.endsWith('\n') ? '\n' : '';
          working = `${working}${sep}${op.text}`;
          applied++;
          break;
        }
        case 'prepend': {
          const sep = working.length > 0 && !op.text.endsWith('\n') ? '\n' : '';
          working = `${op.text}${sep}${working}`;
          applied++;
          break;
        }
        case 'replace': {
          if (!op.find) {
            errors.push(
              `Op ${i} (replace): "find" must be a non-empty string.`,
            );
            break;
          }
          const count = countOccurrences(working, op.find);
          if (count === 0) {
            errors.push(
              `Op ${i} (replace): could not find "${preview(op.find)}" in the document.`,
            );
            break;
          }
          if (count > 1 && !op.replace_all) {
            errors.push(
              `Op ${i} (replace): "${preview(op.find)}" appears ${count} times — pass replace_all:true or use a more specific find string.`,
            );
            break;
          }
          working = op.replace_all
            ? working.split(op.find).join(op.replace)
            : working.replace(op.find, op.replace);
          applied++;
          break;
        }
        case 'insert_after': {
          if (!op.anchor) {
            errors.push(
              `Op ${i} (insert_after): "anchor" must be a non-empty string.`,
            );
            break;
          }
          const count = countOccurrences(working, op.anchor);
          if (count === 0) {
            errors.push(
              `Op ${i} (insert_after): could not find anchor "${preview(op.anchor)}".`,
            );
            break;
          }
          if (count > 1) {
            errors.push(
              `Op ${i} (insert_after): anchor "${preview(op.anchor)}" is ambiguous (${count} matches) — pick a longer/more unique anchor.`,
            );
            break;
          }
          const idx = working.indexOf(op.anchor);
          const cut = idx + op.anchor.length;
          working = `${working.slice(0, cut)}${op.text}${working.slice(cut)}`;
          applied++;
          break;
        }
        case 'insert_before': {
          if (!op.anchor) {
            errors.push(
              `Op ${i} (insert_before): "anchor" must be a non-empty string.`,
            );
            break;
          }
          const count = countOccurrences(working, op.anchor);
          if (count === 0) {
            errors.push(
              `Op ${i} (insert_before): could not find anchor "${preview(op.anchor)}".`,
            );
            break;
          }
          if (count > 1) {
            errors.push(
              `Op ${i} (insert_before): anchor "${preview(op.anchor)}" is ambiguous (${count} matches) — pick a longer/more unique anchor.`,
            );
            break;
          }
          const idx = working.indexOf(op.anchor);
          working = `${working.slice(0, idx)}${op.text}${working.slice(idx)}`;
          applied++;
          break;
        }
        case 'delete': {
          if (!op.find) {
            errors.push(`Op ${i} (delete): "find" must be a non-empty string.`);
            break;
          }
          const count = countOccurrences(working, op.find);
          if (count === 0) {
            errors.push(
              `Op ${i} (delete): could not find "${preview(op.find)}".`,
            );
            break;
          }
          if (count > 1 && !op.delete_all) {
            errors.push(
              `Op ${i} (delete): "${preview(op.find)}" appears ${count} times — pass delete_all:true or use a more specific find string.`,
            );
            break;
          }
          working = op.delete_all
            ? working.split(op.find).join('')
            : working.replace(op.find, '');
          applied++;
          break;
        }
        default: {
          // Exhaustiveness check — if the union gains a new member, TS errors here.
          const _exhaustive: never = op;
          errors.push(
            `Op ${i}: unknown op "${(_exhaustive as { op?: string }).op ?? 'unknown'}".`,
          );
        }
      }
    } catch (err) {
      errors.push(
        `Op ${i} (${op.op}): threw — ${err instanceof Error ? err.message : String(err)}.`,
      );
    }
  }

  return { content: working, errors, appliedCount: applied };
}
